import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { timeout, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { CacheService } from '../cache/cache.service';

@Injectable({
  providedIn: 'root'
})
export class GfdService {

  private readonly API_PATH = '/api';
  private readonly VIP_PATH = '/vip';
  private readonly GFD_VERSION = '/v1/gfd';

  // TTL de 10 minutos para GFD - se invalida automaticamente al editar via ABM
  private readonly GFD_CACHE_TTL = 10 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private cacheService: CacheService,
  ) { }

  get country(): string {
    return this.localStorageService.getRegionSeleccionada?.getCode()?.toUpperCase();
  }

  /**
   * Determina si el usuario está autenticado con VIP (Oidc)
   * Esto afecta qué URL base se usa para operaciones de lectura (GET)
   */
  get isAuthVip(): boolean {
    return this.localStorageService.getIsVip;
  }

  /**
   * Obtiene el ambiente (env) según el tipo de flujo y configuración
   * - RDS: cuando se necesite usar RDS, se debe llamar directamente a RdsService
   * - Si el menuApp actual es VIP (título), usa VIP
   * - Por defecto, DM
   */
  private getEnv(): string {
    // Verificar si el menú actual es VIP por su título
    if (this.localStorageService.isMenuVip()) {
      return this.localStorageService.GPC_CONFIG.env.vip;
    }

    // Por defecto, interno (RDS debe usar rdsService directamente, no gfdService)
    return this.localStorageService.GPC_CONFIG.env.interno;
  }

  /**
   * Obtiene la configuración (config) por defecto.
   * En ambiente local retorna "Default" (para mantener compatibilidad con mocks)
   * En otros ambientes retorna "GFD_Default"
   * NOTA: GFD soporta múltiples configuraciones y títulos dinámicos
   */
  private getDefaultConfig(): string {
    const baseConfig = this.localStorageService.GPC_CONFIG.config.default;

    // En local, usar config simple para mantener compatibilidad con mocks existentes
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      return baseConfig;
    }

    return `GFD_${baseConfig}`;
  }

  /**
   * Construye la URL base para consultas (GET) considerando el flujo de autenticación
   * AUTENTICACIÓN VIP (Oidc): {F_VIP_URL_BASE_API}/vip/gcp-service/v1/gfd
   * AUTENTICACIÓN DM (PingID): {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd
   *
   * NOTA: El environment (VIP/DM) en el path se determina por el menú/config que se consulta,
   * no por el tipo de autenticación
   */
  private buildBaseUrlForRead(): string {
    const urlBaseApi = this.isAuthVip
      ? this.localStorageService.configInit["F_VIP_URL_BASE_API"]
      : this.localStorageService.configInit["F_PINGID_URL_BASE_API"];

    if (this.isAuthVip) {
      // Para usuarios autenticados con VIP (Oidc): extraer solo "gcp-service" de "facade-im/gcp-service"
      const fullMicroservicio = this.localStorageService.configInit["F_URL_MS_IM_GCP"];
      const gcpService = fullMicroservicio ? fullMicroservicio.split('/').pop() : "";
      return `${urlBaseApi}${this.VIP_PATH}/${gcpService}${this.GFD_VERSION}`;
    } else {
      // Para usuarios autenticados con Interno (PingID): usar la ruta completa "facade-im/gcp-service"
      const rutaMicroservicio = (environment.MICROSERVICIO.toString().toLowerCase() === "true")
        ? this.localStorageService.configInit["F_URL_MS_IM_GCP"]
        : "";
      const microservicioPath = rutaMicroservicio ? `/${rutaMicroservicio}` : "";
      return `${urlBaseApi}${microservicioPath}${this.API_PATH}${this.GFD_VERSION}`;
    }
  }

  /**
   * Construye la URL base para operaciones de escritura (POST, DELETE)
   * Siempre usa PINGID (interno), independientemente de si el usuario está en VIP
   * {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd
   */
  private buildBaseUrlForWrite(): string {
    const rutaMicroservicio = (environment.MICROSERVICIO.toString().toLowerCase() === "true")
      ? this.localStorageService.configInit["F_URL_MS_IM_GCP"]
      : "";

    const microservicioPath = rutaMicroservicio ? `/${rutaMicroservicio}` : "";
    return `${this.localStorageService.configInit["F_PINGID_URL_BASE_API"]}${microservicioPath}${this.API_PATH}${this.GFD_VERSION}`;
  }

  /**
   * Consulta GFD Global
   * Método GET
   *
   * Ejemplos de URLs generadas:
   * - Usuario Interno consultando GFD INTERNO (DM):
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd/AR/DOMAIN/SUBDOMAIN/DM/GFD_DEFAULT
   *
   * - Usuario Interno consultando GFD VIP:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd/AR/DOMAIN/SUBDOMAIN/VIP/GFD_DEFAULT
   *
   * - Usuario VIP consultando GFD VIP:
   *   {F_VIP_URL_BASE_API}/vip/gcp-service/v1/gfd/AR/DOMAIN/SUBDOMAIN/VIP/GFD_DEFAULT
   */
  getGfd(domain: string, subdomain: string, config?: string): Observable<any> {
    // Validación de config
    if (!config) {
      config = this.getDefaultConfig();
      console.warn('[GfdService.getGfd] No se proporcionó config, usando default:', config);
    }

    const domainUpper = domain.toUpperCase();
    const subdomainUpper = subdomain.toUpperCase();
    const env = this.getEnv();

    const cacheKey = `gfd|${this.country}|${domainUpper}|${subdomainUpper}|${env}|${config}`;
    const cached = this.cacheService.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const baseUrl = this.buildBaseUrlForRead();

    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      // Modo local: usar mock
      const url = `assets/mock/${this.country.toLowerCase()}/gfd/${domainUpper}_${subdomainUpper}_${config}.json`;
      return this.http.get<any>(url).pipe(
        timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
        tap(response => this.cacheService.set(cacheKey, response, this.GFD_CACHE_TTL))
      );
    }

    const url = `${baseUrl}/${this.country}/${domainUpper}/${subdomainUpper}/${env}/${config}`;

    return this.http.get<any>(url).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(response => this.cacheService.set(cacheKey, response, this.GFD_CACHE_TTL))
    );
  }

  /**
   * Carga GFD Global
   * Método POST (siempre usa interno/PINGID para escritura)
   *
   * Ejemplos:
   * - Carga GFD VIP desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd/charge/AR/DOMAIN/SUBDOMAIN/VIP/GFD_DEFAULT
   *
   * - Carga GFD INTERNO (DM) desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd/charge/AR/DOMAIN/SUBDOMAIN/DM/GFD_DEFAULT
   */
  chargeGfd(domain: string, subdomain: string, body: any, config?: string): Observable<any> {
    // Validación de config
    if (!config) {
      config = this.getDefaultConfig();
      console.warn('[GfdService.chargeGfd] No se proporcionó config, usando default:', config);
    }

    const baseUrl = this.buildBaseUrlForWrite();
    const env = this.getEnv();
    const domainUpper = domain.toUpperCase();
    const subdomainUpper = subdomain.toUpperCase();
    const url = `${baseUrl}/charge/${this.country}/${domainUpper}/${subdomainUpper}/${env}/${config}`;

    return this.http.post<any>(url, body).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(() => this.invalidateGfdCache(domainUpper, subdomainUpper))
    );
  }

  /**
   * Obtiene GFD con información de matchKey extraída del header
   * Útil para búsqueda avanzada con información de hash
   * Reutiliza el método getGfd() existente
   */
  getGfdWithMatchKey(domain: string, subdomain: string, config?: string): Observable<any> {
    return new Observable(observer => {
      // Reutilizar el método getGfd existente
      this.getGfd(domain, subdomain, config).subscribe({
        next: (response) => {
          console.log('[GfdService.getGfdWithMatchKey] Response de getGfd:', response);

          // Extraer matchKey del header
          const header = response?.header?.[0];
          const matchKeyString = header?.matchKey || '';
          const sep = header?.separator || '.';
          const matchKeyFields = matchKeyString ? matchKeyString.split(sep) : [];

          console.log('[GfdService.getGfdWithMatchKey] MatchKey string:', matchKeyString);
          console.log('[GfdService.getGfdWithMatchKey] MatchKey fields:', matchKeyFields);

          // Construir array de fields con información de hash
          const fields = response?.fields || [];
          const matchKeyData = matchKeyFields.map(fieldName => {
            const fieldInfo = fields.find(f => f.name === fieldName);
            const hashValue = fieldInfo?.hash || false;
            console.log(`[GfdService.getGfdWithMatchKey] Campo: ${fieldName}, hash: ${hashValue}`);
            return {
              name: fieldName,
              hash: hashValue
            };
          });

          const result = {
            fields: matchKeyData,
            matchKeyString: matchKeyString,
            allFields: fields,
            header: header
          };

          console.log('[GfdService.getGfdWithMatchKey] Resultado final:', result);
          observer.next(result);
          observer.complete();
        },
        error: (error) => {
          console.error('[GfdService.getGfdWithMatchKey] Error:', error);
          observer.error(error);
        }
      });
    });
  }

  /**
   * Consulta de configuraciones
   * Método GET
   *
   * Ejemplos:
   * - Usuario Interno: {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd/configs/AR/DOMAIN/SUBDOMAIN/VIP
   * - Usuario VIP: {F_VIP_URL_BASE_API}/vip/gcp-service/v1/gfd/configs/AR/DOMAIN/SUBDOMAIN/VIP
   */
  getGfdConfigs(domain: string, subdomain: string): Observable<any> {
    const domainUpper = domain.toUpperCase();
    const subdomainUpper = subdomain.toUpperCase();

    const cacheKey = `gfd-configs|${this.country}|${domainUpper}|${subdomainUpper}`;
    const cached = this.cacheService.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      // Modo local: usar mock
      const url = `assets/mock/${this.country.toLowerCase()}/gfd/${domainUpper}_${subdomainUpper}_configs.json`;
      return this.http.get<any>(url).pipe(
        timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
        tap(response => this.cacheService.set(cacheKey, response, this.GFD_CACHE_TTL))
      );
    }

    const baseUrl = this.buildBaseUrlForRead();
    const env = this.getEnv();
    const url = `${baseUrl}/configs/${this.country}/${domainUpper}/${subdomainUpper}/${env}`;

    return this.http.get<any>(url).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(response => this.cacheService.set(cacheKey, response, this.GFD_CACHE_TTL))
    );
  }

  /**
   * Eliminar field de GFD
   * Método DELETE (siempre usa interno/PINGID para escritura)
   *
   * Ejemplos:
   * - Eliminar de GFD VIP desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/gfd/AR/DOMAIN/SUBDOMAIN/VIP/GFD_DEFAULT/{field}
   */
  deleteGfdField(domain: string, subdomain: string, fieldName: string, config?: string): Observable<any> {
    // Validación de config
    if (!config) {
      config = this.getDefaultConfig();
      console.warn('[GfdService.deleteGfdField] No se proporcionó config, usando default:', config);
    }

    const baseUrl = this.buildBaseUrlForWrite();
    const env = this.getEnv();
    const domainUpper = domain.toUpperCase();
    const subdomainUpper = subdomain.toUpperCase();
    const url = `${baseUrl}/${this.country}/${domainUpper}/${subdomainUpper}/${env}/${config}/${fieldName}`;

    return this.http.delete<any>(url).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(() => this.invalidateGfdCache(domainUpper, subdomainUpper))
    );
  }

  /** Invalida todo el cache de GFD para un domain/subdomain especifico */
  invalidateGfdCache(domain?: string, subdomain?: string): void {
    if (domain && subdomain) {
      this.cacheService.invalidateByPrefix(`gfd|${this.country}|${domain}|${subdomain}`);
      this.cacheService.invalidateByPrefix(`gfd-configs|${this.country}|${domain}|${subdomain}`);
    } else {
      this.cacheService.invalidateByPrefix('gfd|');
      this.cacheService.invalidateByPrefix('gfd-configs|');
    }
  }
}
